import Icon from 'components/Icon';
import * as consts from 'consts/consts';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  PopoverBody,
  Row,
  UncontrolledPopover,
} from 'reactstrap';
import { type BasicPerson } from 'types';
import { useConfirmApi, useProxyParameters } from 'utils/api/ApiHooks';
import ConfirmAPI from 'utils/api/ConfirmAPI';
import {
  DRAG_AND_DROP_ICON,
  DraggableItemTypes,
  getFriendlyErrorMessageIfErrorOccurred,
  useDragAndDrop,
} from 'utils/util/util';
import PeopleEditorOverlay from 'views/Administration/CampaignReports/PeopleEditorOverlay';
import ObjectsDropdown from 'views/Widgets/Dropdowns/ObjectsDropdown';
import EmptyState from 'views/Widgets/EmptyState';
import ValidatedForm from 'views/Widgets/Forms/ValidatedForm';
import RichTextViewer from 'views/Widgets/Inputs/RichTextViewer';
import SwitchInput from 'views/Widgets/Inputs/SwitchInput';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import Loading from 'views/Widgets/Loading';
import ConfirmationDialogModal from 'views/Widgets/Modals/ConfirmationDialogModal';
import FilterablePeopleTable from 'views/Widgets/People/FilterablePeopleTable';
import SurveyChartBuilderModal from '../Charts/SurveyChartBuilderModal';
import SurveyChartView from '../Charts/SurveyChartView';
import {
  CHART_WIDTH_OPTIONS,
  CHART_WIDTH_TO_COL,
  type ChartableQuestionsResponse,
  type ChartDataPreviewResponse,
  type ChartDraft,
  type ChartWidth,
  type CustomSurveyReportData,
  type CustomSurveyReportItemData,
  type SurveyChartConfig,
  type SurveyChartConfigBody,
  type SurveyReportChartData,
} from '../consts';

interface Props {
  reportId: number;
  onClose: () => void;
}

interface FormObj {
  name: string;
}

const PREVIEW_ALL_AUDIENCES = 'all';

// A chart_config id assigned to a draft that hasn't been saved yet - a
// client-only placeholder, never sent to the server as-is (see
// CustomSurveyReportBuilder.handleSave, which routes anything with an id
// below this into chart_creates instead of chart_updates).
const isUnsavedChartId = (id: number): boolean => id < 0;

interface ChartCardProps {
  chart: ChartDraft;
  index: number;
  questionLabel?: string;
  moveChart: (dragIndex: number, hoverIndex: number) => void;
  onEdit: (chart: ChartDraft, index: number) => void;
  onDelete: (chart: ChartDraft) => void;
  onWidthChange: (chart: ChartDraft, width: ChartWidth) => void;
  widthOptions: { id: ChartWidth; name: string }[];
}

const ReportChartCard: FC<ChartCardProps> = ({
  chart,
  index,
  questionLabel,
  moveChart,
  onEdit,
  onDelete,
  onWidthChange,
  widthOptions,
}) => {
  const { formatMessage } = useIntl();
  const [dragAndDropRef, handlerId, drop, isDragging, drag] = useDragAndDrop(
    DraggableItemTypes.TABLE_ROW,
    `chart-${chart.id}`,
    index,
    moveChart,
    true
  );
  // @ts-expect-error - useDragAndDrop is untyped JS
  drag(drop(dragAndDropRef));

  return (
    <Col md={CHART_WIDTH_TO_COL[chart.width]} className="mb-4">
      <Card
        className="h-100"
        innerRef={dragAndDropRef}
        data-handler-id={handlerId}
        style={{ opacity: isDragging ? 0.4 : 1 }}
      >
        <CardHeader className="d-flex align-items-center justify-content-between gap-3">
          <div className="d-flex align-items-center">
            <div
              role="button"
              className="me-2 text-muted flex-shrink-0"
              style={{ cursor: 'grab' }}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.charts.builder.drag_handle_aria',
                defaultMessage: 'Drag to reorder',
              })}
            >
              {DRAG_AND_DROP_ICON}
            </div>
            <div>
              <div className="fw-bold">
                {chart.name || (
                  <FormattedMessage
                    id="app.views.administration.custom_surveys.charts.builder.untitled_chart"
                    defaultMessage="Chart"
                  />
                )}
              </div>
              {questionLabel && (
                <div className="text-muted small">
                  <RichTextViewer model={questionLabel} />
                </div>
              )}
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <ObjectsDropdown
              objects={widthOptions}
              value={chart.width}
              onChange={(e: { target: { value: string } }) =>
                onWidthChange(chart, e.target.value as ChartWidth)
              }
            />
            <button
              className="btn btn-sm btn-light p-1 px-2"
              onClick={() => onEdit(chart, index)}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.charts.builder.edit_chart',
                defaultMessage: 'Edit chart',
              })}
            >
              <Icon icon={consts.ICONS.EDIT} />
            </button>
            <Button
              color="link"
              className="p-0 text-danger"
              onClick={() => onDelete(chart)}
              aria-label={formatMessage({
                id: 'app.views.administration.custom_surveys.charts.builder.delete_chart',
                defaultMessage: 'Delete chart',
              })}
            >
              <Icon icon={consts.ICONS.TRASH} />
            </Button>
          </div>
        </CardHeader>
        <CardBody className="d-flex flex-column justify-content-center">
          {chart.data ? (
            <SurveyChartView config={chart.config} data={chart.data} />
          ) : chart.error ? (
            <EmptyState
              title={
                // Trend charts saved before a statement could be chosen.
                chart.error === 'STATEMENT_REQUIRED'
                  ? formatMessage({
                      id: 'app.views.administration.custom_surveys.charts.builder.statement_required',
                      defaultMessage: 'Choose a statement in Edit chart.',
                    })
                  : formatMessage({
                      id: 'app.views.administration.custom_surveys.charts.builder.chart_error',
                      defaultMessage: 'Could not load chart data.',
                    })
              }
            />
          ) : (
            <Loading />
          )}
        </CardBody>
      </Card>
    </Col>
  );
};

// Full editor for a single report: its name, audience items, and the charts
// shared across all of them. The name form's Save button lives in the
// header, outside the form itself, so it's submitted via formRef - same
// pattern as CustomSurveyBasicsTab's settings form.
const CustomSurveyReportBuilder: FC<Props> = ({ reportId, onClose }) => {
  const { formatMessage } = useIntl();
  const proxyParameters = useProxyParameters();

  const [report, setReport] = useState<CustomSurveyReportData | undefined>(
    undefined
  );
  const [obj, setObj] = useState<FormObj>({ name: '' });
  const [isSaving, setIsSaving] = useState(false);
  const formRef = useRef<HTMLFormElement | null>();

  // Tracked externally rather than left to ValidatedForm's own internal
  // dirty-tracking: since `obj` is lifted out and fed straight back in as
  // `object`, ValidatedForm's own `propsObject`-changed effect would reset
  // its internal flag back to false on every keystroke, and the "unsaved
  // changes" prompt would never fire. hasUnsavedChanges/setHasUnsavedChanges
  // are ValidatedForm's documented escape hatch for exactly this case. Every
  // chart add/edit/delete/reorder below also sets this, so the one prompt
  // covers name, sharing, AND chart edits.
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Unsaved sharing edits to an audience item - viewers/published - overlaid
  // on the fetched report's items, same drafting pattern as chartDrafts
  // below (view-only until "Save report" persists it).
  const [itemDrafts, setItemDrafts] = useState<
    Record<
      number,
      Partial<Pick<CustomSurveyReportItemData, 'viewers' | 'published'>>
    >
  >({});

  const items = useMemo(
    () =>
      (report?.items ?? []).map((item) => ({
        ...item,
        ...(itemDrafts[item.id] ?? {}),
      })),
    [report, itemDrafts]
  );

  const updateItemDraft = useCallback(
    (
      itemId: number,
      patch: Partial<Pick<CustomSurveyReportItemData, 'viewers' | 'published'>>
    ) => {
      setItemDrafts((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] ?? {}), ...patch },
      }));
      setHasUnsavedChanges(true);
    },
    []
  );

  const reportCallback = useCallback(
    (data: CustomSurveyReportData | null, error: unknown) => {
      if (!data || error) return;
      setReport(data);
    },
    []
  );

  const { status } = useConfirmApi<CustomSurveyReportData>({
    method: 'GET',
    url: `custom-survey/reports/${reportId}`,
    callback: reportCallback,
  });

  // Seed the name form once the report first loads - keyed on id so a
  // later re-save (which also updates `report`) doesn't clobber in-progress
  // edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency omission
  useEffect(() => {
    if (report) {
      setObj({ name: report.name });
    }
  }, [report?.id]);

  const handleChange = useCallback((newObj: FormObj) => {
    setObj(newObj);
    setHasUnsavedChanges(true);
  }, []);

  const handleValidate = useCallback(
    (obj: Record<string, unknown>) => {
      const errors: Record<string, string> = {};
      if (!(obj.name as string)?.trim()) {
        errors.name = formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.validation.name_required',
          defaultMessage: 'Report name is required.',
        });
      }
      return errors;
    },
    [formatMessage]
  );

  // ── Charts ──────────────────────────────────────────────────────────

  const [previewItemId, setPreviewItemId] = useState<string>(
    PREVIEW_ALL_AUDIENCES
  );
  const [isChartBuilderOpen, setIsChartBuilderOpen] = useState(false);
  const [editingChart, setEditingChart] = useState<SurveyChartConfig | null>(
    null
  );
  const [deletingChart, setDeletingChart] = useState<ChartDraft | null>(null);

  // The working chart list: every add/edit/delete/reorder/width-change below
  // mutates this directly instead of writing through to the server - array
  // order IS save order. Only "Save report" turns this into
  // chart_creates/chart_updates/chart_deletes (diffed against
  // baselineDraftsRef below) in one PATCH alongside the report's other
  // drafts.
  const [chartDrafts, setChartDrafts] = useState<ChartDraft[]>([]);
  // Ids marked for deletion since the last load/save - only ever real
  // (already-persisted) chart_config ids; deleting an unsaved draft just
  // removes it from chartDrafts directly, nothing to tell the server.
  const [pendingChartDeletes, setPendingChartDeletes] = useState<number[]>([]);
  // The last-synced-with-server snapshot of chartDrafts' identity fields
  // (id/name/width/config, NOT data/error) - used only to diff on Save, not
  // for rendering. Repopulated every time the identity fetch below lands
  // (initial load and after a successful Save).
  const baselineDraftsRef = useRef<
    Pick<ChartDraft, 'id' | 'name' | 'width' | 'config'>[]
  >([]);
  // Client-only ids for not-yet-saved charts, distinct from any real
  // chart_config id (which are always positive).
  const tempChartIdRef = useRef(0);
  const nextTempChartId = useCallback(() => {
    tempChartIdRef.current -= 1;
    return tempChartIdRef.current;
  }, []);

  // Bumped after a successful Save to force the identity fetch below to
  // refetch (picks up real ids for anything just created).
  const [chartsRefreshKey, setChartsRefreshKey] = useState(0);
  const refreshCharts = useCallback(
    () => setChartsRefreshKey((k) => k + 1),
    []
  );

  // Report-scoped, not survey-scoped: a report owner isn't necessarily a
  // survey admin, but is always allowed here (can_manage_custom_survey_report
  // is a superset check on the same underlying permission).
  const { data: chartableQuestionsData, status: chartableQuestionsStatus } =
    useConfirmApi<ChartableQuestionsResponse>({
      method: 'GET',
      url: `custom-survey/surveys/${report?.survey}/chartable-questions`,
      disabled: !report,
    });
  const chartableQuestions = chartableQuestionsData?.questions ?? [];
  const allQuestions = chartableQuestionsData?.all_questions ?? [];

  // An empty list also means "not fetched yet" or "the fetch failed", and
  // only one of those is worth explaining - so the tooltip waits until the
  // survey has actually told us it has no questions.
  const surveyHasNoQuestions =
    chartableQuestionsStatus === 'SUCCESS' && allQuestions.length === 0;

  // So a chart card can show which question it's charting without opening
  // the edit modal. A draft's own computed data (once the preview below
  // resolves) already carries its question.label, but this is the fallback
  // while that's still loading/erroring, and the only source at all for a
  // brand-new chart.
  const questionLabelByUuid = useMemo(() => {
    const map: Record<string, string> = {};
    for (const q of allQuestions) map[q.uuid] = q.label;
    for (const q of chartableQuestions) map[q.uuid] = q.label;
    return map;
  }, [allQuestions, chartableQuestions]);

  const getChartQuestionLabel = useCallback(
    (chart: ChartDraft): string | undefined =>
      chart.data?.question.label ??
      questionLabelByUuid[chart.config.questionUuid],
    [questionLabelByUuid]
  );

  // Rebuilds chartDrafts/baselineDraftsRef from the server's current chart
  // list - identity only (id/name/width/config). Runs on initial load and
  // (via chartsRefreshKey) after every successful Save, so it's also what
  // picks up real ids for anything just created.
  //
  // Reuses the previous draft's already-computed data/error whenever a
  // chart's config comes back unchanged (matched by id, falling back to
  // position for a just-created chart whose id was a temp placeholder
  // moments ago) rather than always nulling them out. A plain Save that
  // doesn't touch any chart's config - the common case: renaming the
  // report, editing sharing, reordering/resizing charts - would otherwise
  // wipe every card back to null with nothing left to refill it, since the
  // live-preview effect below is keyed on config and sees no change to
  // react to: the cards would be stuck on their loading state forever.
  const chartsCallback = useCallback(
    (data: SurveyReportChartData | null, error: unknown) => {
      if (!data || error) return;
      setChartDrafts((prevDrafts) => {
        const prevById = new Map(prevDrafts.map((chart) => [chart.id, chart]));
        const drafts: ChartDraft[] = data.charts.map((chart, index) => {
          const prevMatch = prevById.get(chart.config_id) ?? prevDrafts[index];
          const configUnchanged =
            !!prevMatch &&
            JSON.stringify(prevMatch.config) === JSON.stringify(chart.config);
          return {
            id: chart.config_id,
            name: chart.name,
            width: chart.width,
            config: chart.config,
            data: configUnchanged ? prevMatch.data : null,
            error: configUnchanged ? prevMatch.error : null,
          };
        });
        baselineDraftsRef.current = drafts.map(
          ({ id, name, width, config }) => ({ id, name, width, config })
        );
        return drafts;
      });
      setPendingChartDeletes([]);
    },
    []
  );

  const { status: chartsStatus } = useConfirmApi<SurveyReportChartData>({
    method: 'GET',
    url: `custom-survey/reports/${reportId}/chart-data`,
    params: { _refresh: chartsRefreshKey },
    disabled: !report,
    callback: chartsCallback,
  });

  // A single-item report (e.g. any CUSTOM split, which always produces
  // exactly one item) has no selector to pick an audience from -
  // previewOptions below stays null - so previewItemId would otherwise sit
  // at PREVIEW_ALL_AUDIENCES forever and every preview would omit item_id,
  // scoping against the survey's whole population instead of the one
  // audience this report actually has. Derived at render time (rather than
  // synced into previewItemId via an effect) so the very first preview
  // already carries the right item_id instead of firing once unscoped and
  // once corrected.
  const effectivePreviewItemId =
    report && report.items.length === 1
      ? String(report.items[0].id)
      : previewItemId;

  // Live preview: whenever a draft's own config (question/chart type/etc,
  // the only piece actually sent to chart-data/preview below), or the
  // selected preview audience, changes, recompute every chart's data against
  // the survey without persisting anything. Keyed on a signature of just
  // `config` (not chartDrafts itself, and deliberately not name/width -
  // neither is part of the request, so neither should trigger a refetch) so
  // the data/error this same effect writes back below doesn't re-trigger
  // itself, and a width/name-only edit stays local. chartsCallback above
  // carries over data/error whenever a chart's config comes back unchanged
  // after a refresh, so a Save that doesn't touch config (the common case)
  // never needs this effect to refire at all.
  const chartDraftsSignature = useMemo(
    () => JSON.stringify(chartDrafts.map(({ config }) => config)),
    [chartDrafts]
  );
  const previewRequestIdRef = useRef(0);
  // Set right before a local-only chartDrafts change that shrinks the
  // array (currently: deleting a chart) - see handleDeleteChartConfirm.
  // Removing an entry changes chartDraftsSignature (it's shorter) same as
  // adding/editing one would, but unlike those, nothing about the
  // survivors' configs changed, so there's nothing new for chart-data/
  // preview to compute - skip the refetch it would otherwise trigger.
  const skipNextPreviewRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed on chartDraftsSignature, not chartDrafts itself - see comment above
  useEffect(() => {
    if (skipNextPreviewRef.current) {
      skipNextPreviewRef.current = false;
      return;
    }
    if (chartDrafts.length === 0) return;
    const requestId = ++previewRequestIdRef.current;
    const url = `custom-survey/reports/${reportId}/chart-data/preview${
      effectivePreviewItemId !== PREVIEW_ALL_AUDIENCES
        ? `?item_id=${effectivePreviewItemId}`
        : ''
    }`;
    ConfirmAPI.sendRequestToConfirm(
      'POST',
      url,
      {
        ...proxyParameters,
        charts: chartDrafts.map((chart) => ({ config: chart.config })),
      },
      (data, error) => {
        // Stale response from a superseded edit/audience-switch - ignore.
        if (previewRequestIdRef.current !== requestId || error || !data) return;
        const { results } = data as ChartDataPreviewResponse;
        setChartDrafts((prev) =>
          prev.map((chart, index) => ({
            ...chart,
            data: results[index]?.data ?? null,
            error: results[index]?.error ?? null,
          }))
        );
      }
    );
  }, [chartDraftsSignature, effectivePreviewItemId, reportId, proxyParameters]);

  // Only worth offering a choice once there's more than one audience -
  // a single-audience report has nothing to switch to.
  const previewOptions = useMemo(() => {
    if (!report || report.items.length <= 1) return null;
    return [
      {
        id: PREVIEW_ALL_AUDIENCES,
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.charts.builder.preview_all',
          defaultMessage: 'All audiences',
        }),
      },
      ...report.items.map((item) => ({
        id: String(item.id),
        name:
          item.name ||
          formatMessage({
            id: 'app.views.administration.custom_surveys.reports.untitled',
            defaultMessage: 'Report',
          }),
      })),
    ];
  }, [report, formatMessage]);

  // Reset back to "everyone" if the selected item no longer exists (e.g. the
  // report's split changed and regenerated its items).
  useEffect(() => {
    if (
      previewItemId !== PREVIEW_ALL_AUDIENCES &&
      report &&
      !report.items.some((item) => String(item.id) === previewItemId)
    ) {
      setPreviewItemId(PREVIEW_ALL_AUDIENCES);
    }
  }, [previewItemId, report]);

  const handleAddChartClick = useCallback(() => {
    setEditingChart(null);
    setIsChartBuilderOpen(true);
  }, []);

  const handleEditChartClick = useCallback(
    (chart: ChartDraft, index: number) => {
      setEditingChart({
        id: chart.id,
        report: reportId,
        name: chart.name,
        order: index,
        width: chart.width,
        config: chart.config,
      });
      setIsChartBuilderOpen(true);
    },
    [reportId]
  );

  // This doubles as ModalEditor's `toggle` prop, so on a successful submit
  // it fires *before* the actual onSave callback (ModalEditor closes itself
  // first, then calls back once the close transition finishes). Don't clear
  // editingChart here - doing so raced handleSaveChart's read of it, turning
  // every edit into a create. Every path that opens the modal
  // (handleAddChartClick/handleEditChartClick) already sets it explicitly
  // beforehand, so there's nothing to reset on close.
  const handleCloseChartBuilder = useCallback(() => {
    setIsChartBuilderOpen(false);
  }, []);

  const handleSaveChart = useCallback(
    (payload: { name: string; config: SurveyChartConfigBody }) => {
      if (editingChart) {
        setChartDrafts((prev) =>
          prev.map((chart) =>
            chart.id === editingChart.id
              ? { ...chart, name: payload.name, config: payload.config }
              : chart
          )
        );
      } else {
        setChartDrafts((prev) => [
          ...prev,
          {
            id: nextTempChartId(),
            name: payload.name,
            width: 'HALF',
            config: payload.config,
            data: null,
            error: null,
          },
        ]);
      }
      setHasUnsavedChanges(true);
      handleCloseChartBuilder();
    },
    [editingChart, nextTempChartId, handleCloseChartBuilder]
  );

  const handleDeleteChartClick = useCallback((chart: ChartDraft) => {
    setDeletingChart(chart);
  }, []);

  const handleCloseDeleteChartModal = useCallback(() => {
    setDeletingChart(null);
  }, []);

  const handleDeleteChartConfirm = useCallback(() => {
    if (!deletingChart) return;
    if (!isUnsavedChartId(deletingChart.id)) {
      setPendingChartDeletes((prev) => [...prev, deletingChart.id]);
    }
    skipNextPreviewRef.current = true;
    setChartDrafts((prev) => prev.filter((c) => c.id !== deletingChart.id));
    setHasUnsavedChanges(true);
    handleCloseDeleteChartModal();
  }, [deletingChart, handleCloseDeleteChartModal]);

  const handleWidthChange = useCallback(
    (chart: ChartDraft, width: ChartWidth) => {
      setChartDrafts((prev) =>
        prev.map((c) => (c.id === chart.id ? { ...c, width } : c))
      );
      setHasUnsavedChanges(true);
    },
    []
  );

  const moveChart = useCallback((dragIndex: number, hoverIndex: number) => {
    setChartDrafts((prev) => {
      const next = [...prev];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(hoverIndex, 0, moved);
      return next;
    });
    setHasUnsavedChanges(true);
  }, []);

  const widthOptions = useMemo(
    () => CHART_WIDTH_OPTIONS(formatMessage),
    [formatMessage]
  );

  // The report's name, every pending item-sharing edit, and every pending
  // chart add/edit/delete/reorder (diffed against baselineDraftsRef, so only
  // what actually changed is sent) are all saved together in one PATCH -
  // the backend's CustomSurveyReportSerializer.update() applies all of it
  // (creates, updates, deletes, and the item edits) inside a single
  // transaction. Permission-equivalent to PATCHing a chart_config or
  // report-item directly (both mirror can_manage_custom_survey_report on
  // the parent report), so folding them in here doesn't reach any further
  // than those other endpoints already could.
  const handleSave = useCallback(() => {
    if (!report) return;
    setIsSaving(true);

    const itemUpdates = Object.fromEntries(
      Object.entries(itemDrafts).map(([itemId, draft]) => [
        itemId,
        {
          ...(draft.published !== undefined
            ? { published: draft.published }
            : {}),
          ...(draft.viewers !== undefined
            ? { viewers: draft.viewers.map((person) => person.id) }
            : {}),
        },
      ])
    );

    const baseline = baselineDraftsRef.current;
    const baselineById = new Map(baseline.map((chart) => [chart.id, chart]));
    const baselineOrderById = new Map(
      baseline.map((chart, index) => [chart.id, index])
    );

    const chartCreates: {
      name: string;
      width: ChartWidth;
      order: number;
      config: SurveyChartConfigBody;
    }[] = [];
    const chartUpdates: Record<
      number,
      Partial<{
        name: string;
        width: ChartWidth;
        order: number;
        config: SurveyChartConfigBody;
      }>
    > = {};

    chartDrafts.forEach((chart, index) => {
      if (isUnsavedChartId(chart.id)) {
        chartCreates.push({
          name: chart.name,
          width: chart.width,
          order: index,
          config: chart.config,
        });
        return;
      }

      const original = baselineById.get(chart.id);
      const body: Partial<{
        name: string;
        width: ChartWidth;
        order: number;
        config: SurveyChartConfigBody;
      }> = {};
      if (!original || chart.name !== original.name) body.name = chart.name;
      if (!original || chart.width !== original.width) body.width = chart.width;
      if (!original || baselineOrderById.get(chart.id) !== index) {
        body.order = index;
      }
      if (
        !original ||
        JSON.stringify(chart.config) !== JSON.stringify(original.config)
      ) {
        body.config = chart.config;
      }
      if (Object.keys(body).length > 0) {
        chartUpdates[chart.id] = body;
      }
    });

    ConfirmAPI.sendRequestToConfirm(
      'PATCH',
      `custom-survey/reports/${report.id}`,
      {
        ...proxyParameters,
        name: obj.name,
        chart_creates: chartCreates,
        chart_updates: chartUpdates,
        chart_deletes: pendingChartDeletes,
        item_updates: itemUpdates,
      },
      (data, error, hardErrorMessage) => {
        if (error || hardErrorMessage) {
          setIsSaving(false);
          toast.error(
            getFriendlyErrorMessageIfErrorOccurred(error, hardErrorMessage) ??
              formatMessage({
                id: 'app.views.administration.custom_surveys.reports.builder.save_error',
                defaultMessage: 'Failed to save report.',
              })
          );
          return;
        }

        setIsSaving(false);
        const updatedReport = data as CustomSurveyReportData;
        setReport({
          ...updatedReport,
          // Overlay the just-saved item drafts - cheap belt-and-suspenders
          // against any subtle ordering surprise in how the response was
          // serialized, since the PATCH already applied them server-side.
          items: updatedReport.items.map((item) =>
            itemDrafts[item.id] ? { ...item, ...itemDrafts[item.id] } : item
          ),
        });
        setItemDrafts({});
        setHasUnsavedChanges(false);
        // Resyncs chartDrafts/baselineDraftsRef with real ids for anything
        // just created (and clears pendingChartDeletes/chart identity via
        // chartsCallback).
        refreshCharts();
        toast.success(
          formatMessage({
            id: 'app.views.administration.custom_surveys.reports.builder.save_success',
            defaultMessage: 'Report saved.',
          })
        );
      }
    );
  }, [
    report,
    obj,
    chartDrafts,
    pendingChartDeletes,
    itemDrafts,
    proxyParameters,
    formatMessage,
    refreshCharts,
  ]);

  const inputs = useMemo(
    () => [
      {
        name: 'name',
        type: INPUT_TYPES.TEXT,
        label: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.name_label',
          defaultMessage: 'Report name',
        }),
        required: true,
      },
    ],
    [formatMessage]
  );

  const backButton = (
    <Button color="link" onClick={onClose}>
      <Icon className="me-1" icon={consts.ICONS.ARROW_LEFT} />
      {formatMessage({
        id: 'app.views.administration.custom_surveys.reports.builder.back',
        defaultMessage: 'Back to reports',
      })}
    </Button>
  );

  const itemColumns = useMemo(
    () => [
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.items.col.name',
          defaultMessage: 'Name',
        }),
        field: 'name',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.items.col.viewers',
          defaultMessage: 'Shared with',
        }),
        field: 'viewers',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.items.col.published',
          defaultMessage: 'Published',
        }),
        field: 'published',
        className: 'col-1 text-start',
        sortable: false,
      },
      {
        name: formatMessage({
          id: 'app.views.administration.custom_surveys.reports.builder.items.col.actions',
          defaultMessage: 'Actions',
        }),
        field: 'actions',
        className: 'col-auto text-center',
        sortable: false,
      },
    ],
    [formatMessage]
  );

  const itemShareLink = useCallback(
    (itemId: number): string =>
      `${window.location.origin}${consts
        .CUSTOM_SURVEY_REPORT_ITEM(formatMessage)
        .path.replace(':itemId', String(itemId))}`,
    [formatMessage]
  );

  const itemRows = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        name: item.name,
        viewers: (
          <PeopleEditorOverlay
            selectedPeople={item.viewers}
            editTitle={formatMessage({
              id: 'app.views.administration.custom_surveys.reports.builder.items.col.viewers',
              defaultMessage: 'Shared with',
            })}
            size="sm"
            onUpdate={(people: BasicPerson[]) =>
              updateItemDraft(item.id, { viewers: people })
            }
          />
        ),
        published: (
          <SwitchInput
            centered
            value={item.published}
            onChange={(value) =>
              updateItemDraft(item.id, { published: value as boolean })
            }
            ariaLabel={formatMessage({
              id: 'app.views.administration.custom_surveys.reports.builder.items.col.published',
              defaultMessage: 'Published',
            })}
          />
        ),
        actions: (
          <Button
            color="link"
            className="p-0"
            tag="a"
            href={itemShareLink(item.id)}
            target="_blank"
            rel="noreferrer"
          >
            <FormattedMessage
              id="app.views.administration.custom_surveys.reports.builder.items.open"
              defaultMessage="Open"
            />
          </Button>
        ),
      })),
    [items, formatMessage, updateItemDraft, itemShareLink]
  );

  if (status === 'LOADING') {
    return <Loading />;
  }

  if (status === 'ERROR' || !report) {
    return (
      <div className="d-flex flex-column gap-3">
        {backButton}
        <Card>
          <CardBody>
            <EmptyState
              title={formatMessage({
                id: 'app.views.administration.custom_surveys.reports.builder.load_error',
                defaultMessage: 'Could not load this report.',
              })}
            />
          </CardBody>
        </Card>
      </div>
    );
  }

  return (
    <div className="d-flex flex-column gap-3 mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        {backButton}
        <Button
          color="primary"
          onClick={() => formRef.current?.props.onSubmit()}
          disabled={isSaving}
        >
          {isSaving
            ? formatMessage({
                id: 'app.views.administration.custom_surveys.reports.builder.saving',
                defaultMessage: 'Saving...',
              })
            : formatMessage({
                id: 'app.views.administration.custom_surveys.reports.builder.save',
                defaultMessage: 'Save report',
              })}
        </Button>
      </div>

      <Card>
        <CardBody>
          <ValidatedForm
            formRef={formRef}
            object={obj}
            inputs={inputs}
            onChange={handleChange}
            onValidate={handleValidate}
            noValidate
            renderForm={(children: React.ReactNode) => <>{children}</>}
            callback={handleSave}
            hasUnsavedChanges={hasUnsavedChanges}
            setHasUnsavedChanges={setHasUnsavedChanges}
          />
        </CardBody>
      </Card>

      <div>
        <h4 className="mb-1">
          <FormattedMessage
            id="app.views.administration.custom_surveys.reports.audiences_heading"
            defaultMessage="Reports"
          />
        </h4>
        <div className="text-muted small">
          <FormattedMessage
            id="app.views.administration.custom_surveys.reports.audiences_hint"
            defaultMessage="Each report renders the shared charts for its own audience. Share each one independently."
          />
        </div>
      </div>

      {itemRows.length === 0 ? (
        <Card>
          <CardBody className="text-center text-muted">
            <FormattedMessage
              id="app.views.administration.custom_surveys.reports.no_audiences"
              defaultMessage="No audiences match this split (small groups are omitted)."
            />
          </CardBody>
        </Card>
      ) : (
        <FilterablePeopleTable
          rows={itemRows}
          columns={itemColumns}
          hideFilters={true}
          hideExportButton={true}
        />
      )}

      <div className="d-flex justify-content-between align-items-center">
        <h4 className="mb-0">
          <FormattedMessage
            id="app.views.administration.custom_surveys.charts.builder.heading"
            defaultMessage="Charts"
          />
        </h4>
        <div className="d-flex align-items-center gap-3">
          {previewOptions && (
            <>
              <label className="form-label text-muted small mb-0">
                <FormattedMessage
                  id="app.views.administration.custom_surveys.charts.builder.preview_label"
                  defaultMessage="Preview"
                />
              </label>
              <ObjectsDropdown
                objects={previewOptions}
                value={previewItemId}
                onChange={(e: { target: { value: string } }) =>
                  setPreviewItemId(e.target.value)
                }
              />
            </>
          )}
          {/* A disabled button fires no hover events of its own, so the
              popover targets the wrapper instead. */}
          <span id="add-chart-btn">
            <Button
              color="primary"
              onClick={handleAddChartClick}
              disabled={allQuestions.length === 0}
            >
              <FormattedMessage
                id="app.views.administration.custom_surveys.charts.builder.add_chart"
                defaultMessage="Add chart"
              />
            </Button>
          </span>
          {surveyHasNoQuestions && (
            <UncontrolledPopover
              placement="bottom"
              trigger="hover"
              target="add-chart-btn"
            >
              <PopoverBody className="text-dark">
                <FormattedMessage
                  id="app.views.administration.custom_surveys.charts.builder.add_chart.disabled_no_questions"
                  defaultMessage="Add at least one question to this survey before building charts."
                />
              </PopoverBody>
            </UncontrolledPopover>
          )}
        </div>
      </div>

      {chartsStatus === 'LOADING' ? (
        <Loading />
      ) : chartDrafts.length === 0 ? (
        <Card>
          <CardBody className="text-center text-muted">
            <FormattedMessage
              id="app.views.administration.custom_surveys.charts.builder.no_charts"
              defaultMessage="No charts yet. Add a chart to this report."
            />
          </CardBody>
        </Card>
      ) : (
        <Row>
          {chartDrafts.map((chart, index) => (
            <ReportChartCard
              key={chart.id}
              chart={chart}
              index={index}
              questionLabel={getChartQuestionLabel(chart)}
              moveChart={moveChart}
              onEdit={handleEditChartClick}
              onDelete={handleDeleteChartClick}
              onWidthChange={handleWidthChange}
              widthOptions={widthOptions}
            />
          ))}
        </Row>
      )}

      <SurveyChartBuilderModal
        isOpen={isChartBuilderOpen}
        toggle={handleCloseChartBuilder}
        onSave={handleSaveChart}
        chartableQuestions={chartableQuestions}
        allQuestions={allQuestions}
        existing={editingChart}
      />

      {deletingChart && (
        <ConfirmationDialogModal
          isOpen={!!deletingChart}
          onClosed={handleCloseDeleteChartModal}
          toggle={handleCloseDeleteChartModal}
          confirmCallback={handleDeleteChartConfirm}
          title={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.builder.delete_chart.title',
            defaultMessage: 'Delete Chart',
          })}
          description={formatMessage(
            {
              id: 'app.views.administration.custom_surveys.charts.builder.delete_chart.confirm',
              defaultMessage:
                'Are you sure you want to delete {name}? This action cannot be undone',
            },
            {
              name:
                deletingChart.name ||
                formatMessage({
                  id: 'app.views.administration.custom_surveys.charts.builder.untitled_chart',
                  defaultMessage: 'Chart',
                }),
            }
          )}
          confirmText={formatMessage({
            id: 'app.views.administration.custom_surveys.charts.builder.delete_chart.confirm_button',
            defaultMessage: 'Delete',
          })}
        />
      )}
    </div>
  );
};

export default CustomSurveyReportBuilder;
